import React from 'react';
import {
  Box,
  Button,
  ButtonText,
  Center,
  Heading,
  HStack,
  Spinner,
  Text,
  VStack,
} from '@gluestack-ui/themed';
import { useTranslation } from 'react-i18next';

type OralExamErrorTone = 'error' | 'warning' | 'neutral';

type OralExamErrorStateBaseProps = {
  title: string;
  message?: string;
  tone?: OralExamErrorTone;
  testID?: string;
  retryTestID?: string;
  /** Inline error from the last retry/primary action (distinct from `message`). */
  actionError?: string | null;
  isRetryPending?: boolean;
};

export type OralExamErrorStateProps = OralExamErrorStateBaseProps & (
  | { retry: () => void | Promise<void>; retryLabel?: string }
  | { leave: () => void; leaveLabel?: string }
  | { retry: () => void | Promise<void>; retryLabel?: string; leave: () => void; leaveLabel?: string }
);

/** Internal render view — all action fields optional so the component can destructure safely. */
type OralExamErrorStateInternal = OralExamErrorStateBaseProps & {
  retry?: () => void | Promise<void>;
  retryLabel?: string;
  leave?: () => void;
  leaveLabel?: string;
};

function toneHeadingColor(tone: OralExamErrorTone): string {
  if (tone === 'error') return '$statusError';
  if (tone === 'warning') return '$statusWarning';
  return '$textHeading';
}

/**
 * Full-screen oral exam error surface. Props require at least one recovery action
 * so dead-end error screens are unrepresentable at the type level.
 */
export function OralExamErrorState(props: OralExamErrorStateProps) {
  const { t } = useTranslation();
  const {
    title,
    message,
    tone = 'error',
    testID = 'oral-exam-error-state',
    retryTestID = 'oral-exam-error-retry',
    actionError,
    isRetryPending = false,
    retry,
    retryLabel: retryLabelProp,
    leave,
    leaveLabel: leaveLabelProp,
  } = props as OralExamErrorStateInternal;

  const retryLabel = retry
    ? (retryLabelProp ?? t('oralExam.errorState.retry', { defaultValue: 'Try again' }))
    : undefined;
  const leaveLabel = leave
    ? (leaveLabelProp ?? t('oralExam.errorState.leave', { defaultValue: 'Leave' }))
    : undefined;

  return (
    <Center flex={1} bg="$bgApp" p="$8" testID={testID}>
      <VStack space="xl" alignItems="center" maxWidth={420}>
        <VStack space="md" alignItems="center">
          <Heading size="xl" textAlign="center" color={toneHeadingColor(tone)}>
            {title}
          </Heading>
          {message ? (
            <Text size="md" color="$textBody" textAlign="center" lineHeight={24}>
              {message}
            </Text>
          ) : null}
        </VStack>

        {actionError ? (
          <Text size="sm" color="$statusError" textAlign="center" testID="oral-exam-error-action">
            {actionError}
          </Text>
        ) : null}

        <VStack space="md" width="100%" mt="$2">
          {retry ? (
            <Button
              action="primary"
              size="lg"
              onPress={async () => {
                if (isRetryPending) return;
                await Promise.resolve(retry());
              }}
              isDisabled={isRetryPending}
              testID={retryTestID}
            >
              {isRetryPending ? (
                <HStack space="xs" alignItems="center">
                  <Spinner size="small" color="$white" />
                  <ButtonText>{retryLabel}</ButtonText>
                </HStack>
              ) : (
                <ButtonText>{retryLabel}</ButtonText>
              )}
            </Button>
          ) : null}
          {leave ? (
            <Button
              variant="outline"
              size="lg"
              onPress={leave}
              testID="oral-exam-error-leave"
            >
              <ButtonText>{leaveLabel}</ButtonText>
            </Button>
          ) : null}
        </VStack>
      </VStack>
    </Center>
  );
}

type OralExamPollReconnectingBannerProps = {
  message?: string;
};

/** Non-blocking passive banner while run-state poll is in error but last-good state is shown. */
export function OralExamPollReconnectingBanner({ message }: OralExamPollReconnectingBannerProps) {
  const { t } = useTranslation();

  return (
    <Box
      bg="$statusWarningSubtle"
      borderLeftWidth={4}
      borderLeftColor="$statusWarning"
      px="$4"
      py="$2"
      testID="oral-exam-poll-reconnecting-banner"
    >
      <Text size="sm" color="$textBody" fontWeight="semibold">
        {message ?? t('oralExam.pollReconnecting', { defaultValue: 'Reconnecting to server…' })}
      </Text>
    </Box>
  );
}
